import {
    FC,
    FormEvent,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useToggle } from 'react-use';

import { ErrorLabel } from '../../../components';
import {
    Button,
    DateInput,
    PasswordInput,
    RecaptchaDisclaimer,
    TextInput,
} from '../../../compositions';
import { Customer } from '../../../entities/@account/Customer/Customer';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { AddressType, CustomerAddress } from '../../../entities/Address/Address';
import { formatDateAsInputValue, transformIsoStringToDate } from '../../../helpers/date';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { CustomerFormErrors, validateCustomerForm } from './validations';

import './CustomerForm.scss';

interface CustomerFormProps extends FormProps<Customer> {
    customer: Customer;
    onCancel: () => void;
    className?: string;
}

export const CustomerForm: FC<CustomerFormProps> = ({
    isLoading,
    customer,
    onSubmit,
    onCancel,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formRef = useRef<HTMLFormElement>(null);

    const billingAddress = useMemo<CustomerAddress | undefined>(() => (
        customer.addresses.find(address => address.type === AddressType.billing)
    ), [customer]);

    const [firstName, setFirstName] = useState(customer.firstName);
    const [lastName, setLastName] = useState(customer.lastName);
    const [email, setEmail] = useState(customer.email);
    const [showPassword, setShowPassword] = useToggle(false);
    const [password, setPassword] = useState<string | undefined>('');
    const [dateOfBirth, setDateOfBirth] = useState(customer.birthday);
    const [phoneNumber, setPhoneNumber] = useState(customer.phoneNumber || billingAddress?.phoneNumber || '');

    const [formErrors, setFormErrors] = useState<CustomerFormErrors>({});
    const [botDetected, setBotDetected] = useState<boolean>(false);

    const today = formatDateAsInputValue();

    useEffect((): void => {
        setShowPassword(email !== customer.email);
    }, [email]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!executeRecaptcha) {
            setBotDetected(true);
            return;
        }

        const formattedDate = dateOfBirth
            ? transformIsoStringToDate(dateOfBirth)
            : undefined;

        const formData: Customer = {
            id: customer.id,
            addresses: customer.addresses,
            firstName,
            lastName,
            email,
            password,
            birthday: formattedDate,
            phoneNumber,
            company: undefined, // TODO: Add company when feature is requested (API must also be updated)
        };

        const [errors, hasErrors] = validateCustomerForm(formData);
        const token = await executeRecaptcha();

        setFormErrors(errors);
        setBotDetected(!token);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`customer-form ${className}`}
        >
            <div className="customer-form__form-section">
                <fieldset className="customer-form__fieldset">
                    <TextInput
                        required
                        label={trans('form.firstName')}
                        value={firstName}
                        error={formErrors.firstName}
                        disabled={isLoading}
                        onChange={setFirstName}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />

                    <TextInput
                        required
                        label={trans('form.lastName')}
                        value={lastName}
                        error={formErrors.lastName}
                        disabled={isLoading}
                        onChange={setLastName}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />

                    <TextInput
                        required
                        type="email"
                        inputMode="email"
                        label={trans('form.email')}
                        value={email}
                        error={formErrors.email}
                        disabled={isLoading}
                        onChange={setEmail}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />

                    {showPassword && (
                        <PasswordInput
                            required
                            autoComplete="current-password"
                            label={trans('form.password')}
                            value={password}
                            error={formErrors.password}
                            onChange={setPassword}
                            className="customer-form__input"
                            labelClassName="customer-form__input-label"
                            fieldClassName="customer-form__input-field"
                        />
                    )}

                    <DateInput
                        required
                        label={trans('form.dateOfBirth')}
                        max={today}
                        value={dateOfBirth}
                        disabled={isLoading}
                        onChange={setDateOfBirth}
                        error={formErrors.birthday}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />

                    <TextInput
                        required
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        label={trans('form.phoneNumber')}
                        value={phoneNumber}
                        error={formErrors.phoneNumber}
                        disabled={isLoading}
                        onChange={setPhoneNumber}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />

                    <RecaptchaDisclaimer className="customer-form__recaptcha-disclaimer" />

                    {botDetected && (
                        <ErrorLabel text={trans('errors.unknownError')} />
                    )}
                </fieldset>
            </div>

            {/* TODO: This section is now hidden. Enable this when company is available in API */}
            <div className="customer-form__form-section customer-form__form-section--company">
                <fieldset className="customer-form__fieldset">
                    <TextInput
                        label={trans('form.companyName')}
                        value="TODO"
                        disabled={isLoading}
                        onChange={() => {}}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />

                    <TextInput
                        label={trans('form.vatNumber')}
                        value="TODO"
                        disabled={isLoading}
                        onChange={() => {}}
                        className="customer-form__input"
                        labelClassName="customer-form__input-label"
                        fieldClassName="customer-form__input-field"
                    />
                </fieldset>
            </div>

            <div className="customer-form__buttons-wrapper">
                <Button
                    isSmall
                    type="button"
                    text={trans('common.close')}
                    onClick={onCancel}
                    className="customer-form__close-button"
                />

                <Button
                    isSmall
                    type="submit"
                    text={trans('common.save')}
                />
            </div>
        </form>
    );
};
